export default function BestBiryaniOmaha() {
  const faqSchema = `{
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@graph": [
      {
        "@type": "Question",
        "@id": "https://biryani2go.us/blog/best-biryani-in-omaha#what-is-dum-biryani",
        "name": "What is dum-cooked biryani?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Dum cooking is an ancient technique where a sealed pot (handi) is filled with marinated meat or vegetables and parboiled rice, then sealed with dough and left to steam on low heat. No stirring, no opening the lid — the steam from the ingredients cooks everything together until the rice, meat, and spices become one. It takes hours, but the result is biryani that tastes completely different from a pressure-cooked or microwave version."
        }
      },
      {
        "@type": "Question",
        "@id": "https://biryani2go.us/blog/best-biryani-in-omaha#veg-non-veg",
        "name": "Does Biryani2Go make vegetarian biryani?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, absolutely. Our vegetarian biryanis get the same care and sealing as our meat versions. We layer fresh vegetables, paneer, or legumes with the same fragrant basmati and spices, seal them in a handi, and steam them the same slow way. Check our menu for current options."
        }
      },
      {
        "@type": "Question",
        "@id": "https://biryani2go.us/blog/best-biryani-in-omaha#how-long-does-biryani-take",
        "name": "How long does biryani take to cook?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Biryani ordered today will be ready in about 30-45 minutes for pickup, or delivered within 45-60 minutes depending on distance. That's because we start from scratch when you order — marinating, cooking rice, layering, sealing, and steaming — never pre-made. This is why it tastes fresh every time."
        }
      },
      {
        "@type": "Question",
        "@id": "https://biryani2go.us/blog/best-biryani-in-omaha#fast-biryani-vs-dum",
        "name": "Why doesn't fast biryani taste like Biryani2Go?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Most fast biryani is made in pressure cookers or cooked in open pots with stirring. This speeds things up (under 15 minutes instead of hours), but the rice stays separate from the spices, and the flavors never meld the way they do in dum cooking. It looks like biryani, but it tastes rushed. Dum-sealed biryani has a layered, integrated flavor that you can't fake with shortcuts."
        }
      }
    ]
  }`;

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqSchema }} />
      <div className="space-y-6 text-base md:text-lg leading-relaxed text-ink-2 max-w-[68ch]">
        <p>
          If you live in Omaha or Council Bluffs and you're searching for "best biryani near me," you've probably noticed that most restaurants serve what you might call fast biryani — made in a pressure cooker, ready in 15 minutes, and tasting like the rice and spices never quite made peace with each other. Real dum-cooked biryani tastes completely different. Here's how to spot the difference, and where to find it.
        </p>

        <h2 className="font-display font-semibold text-2xl md:text-3xl text-maroon !mt-10 !mb-2">
          The Problem With Fast Biryani
        </h2>
        <p>
          Speed is the enemy of flavor when it comes to biryani. The whole point of the dum cooking method is time — hours of low-heat steaming that lets the basmati rice absorb spices, ghee, and marinated meat juice all at once, without stirring. You can't rush that.
        </p>
        <p>
          When a restaurant uses a pressure cooker to cut cooking time down to 15 minutes, what you get is technically cooked biryani, but it's not integrated biryani. The rice stays separate. The spices sit on top. You taste them in layers instead of as one thing. And if they're stirring it halfway through (which many places do), you're losing the whole point — the layers are what make dum biryani special.
        </p>

        <h2 className="font-display font-semibold text-2xl md:text-3xl text-maroon !mt-10 !mb-2">
          What Real Dum-Cooked Biryani Looks Like
        </h2>
        <p>
          Real dum biryani starts with a handi — a sealed earthenware or copper pot. Here's what goes in:
        </p>
        <ul className="list-disc list-inside space-y-2 my-4">
          <li>Parboiled basmati rice (cooked just halfway)</li>
          <li>Meat or vegetables that have been marinating overnight in yogurt and spices</li>
          <li>Fried onions, mint, and ghee between each layer</li>
          <li>The handi is sealed with dough to lock in steam</li>
          <li>It sits on low heat for 45 minutes to several hours</li>
        </ul>
        <p>
          The result is biryani where every grain of rice has absorbed the flavor of the meat and spices. No layer tastes different from another. This is what you should be looking for.
        </p>

        <h2 className="font-display font-semibold text-2xl md:text-3xl text-maroon !mt-10 !mb-2">
          How to Find Real Dum Biryani in Omaha
        </h2>
        <p>
          Ask a few questions before you order:
        </p>
        <div className="space-y-4 my-4">
          <div>
            <h3 className="font-semibold text-maroon mb-1">"How long does biryani take?"</h3>
            <p>
              If they say under 20 minutes, it's not dum-cooked. A real dum biryani order takes 30-45 minutes minimum, because it's made from scratch when you order — no shortcuts.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-maroon mb-1">"Is it sealed in a handi?"</h3>
            <p>
              Real dum biryani is made in sealed pots. If they're not talking about handis or sealing the pot with dough, they're not doing dum cooking.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-maroon mb-1">"Do you make it to order?"</h3>
            <p>
              Dum biryani can't be made in bulk and reheated. It has to start when you order it. If they have it sitting in warmers, it's not real dum biryani.
            </p>
          </div>
        </div>

        <h2 className="font-display font-semibold text-2xl md:text-3xl text-maroon !mt-10 !mb-2">
          Biryani2Go: Dum-Cooked, Made to Order
        </h2>
        <p>
          At Biryani2Go, every biryani — whether it's chicken, lamb, mutton, or vegetarian — is sealed in a handi and steamed the traditional way. It takes time. Every order is made to order, never pre-made. And we pack it hot for pickup or delivery across Omaha and Council Bluffs.
        </p>
        <p>
          You can <a href="/menu" className="text-ember font-semibold underline hover:no-underline">check our current menu</a> to see what's steaming right now, or <a href="/contact" className="text-ember font-semibold underline hover:no-underline">reach out directly</a> if you have questions about a specific biryani style.
        </p>

        <h2 className="font-display font-semibold text-2xl md:text-3xl text-maroon !mt-10 !mb-2">
          Regional Biryani Styles
        </h2>
        <p>
          Biryani varies by region in India. Hyderabadi biryani (from Hyderabad) is known for a short-grain rice and a more meat-forward approach. Lucknowi is lighter and more delicate. Kolkata style uses potato and egg. We work with regional recipes to offer variety, so exploring our menu is part of the fun.
        </p>

        <h2 className="font-display font-semibold text-2xl md:text-3xl text-maroon !mt-10 !mb-2">
          Frequently Asked Questions
        </h2>

        <div className="space-y-5">
          <div>
            <h3 className="font-display font-semibold text-lg text-maroon mb-1.5">
              What is dum-cooked biryani?
            </h3>
            <p>
              Dum cooking is an ancient technique where a sealed pot (handi) is filled with marinated meat or vegetables and parboiled rice, then sealed with dough and left to steam on low heat. No stirring, no opening the lid — the steam from the ingredients cooks everything together until the rice, meat, and spices become one. It takes hours, but the result is biryani that tastes completely different from a pressure-cooked or microwave version.
            </p>
          </div>
          <div>
            <h3 className="font-display font-semibold text-lg text-maroon mb-1.5">
              Does Biryani2Go make vegetarian biryani?
            </h3>
            <p>
              Yes, absolutely. Our vegetarian biryanis get the same care and sealing as our meat versions. We layer fresh vegetables, paneer, or legumes with the same fragrant basmati and spices, seal them in a handi, and steam them the same slow way. Check our menu for current options.
            </p>
          </div>
          <div>
            <h3 className="font-display font-semibold text-lg text-maroon mb-1.5">
              How long does biryani take to cook?
            </h3>
            <p>
              Biryani ordered today will be ready in about 30-45 minutes for pickup, or delivered within 45-60 minutes depending on distance. That's because we start from scratch when you order — marinating, cooking rice, layering, sealing, and steaming — never pre-made. This is why it tastes fresh every time.
            </p>
          </div>
          <div>
            <h3 className="font-display font-semibold text-lg text-maroon mb-1.5">
              Why doesn't fast biryani taste like Biryani2Go?
            </h3>
            <p>
              Most fast biryani is made in pressure cookers or cooked in open pots with stirring. This speeds things up (under 15 minutes instead of hours), but the rice stays separate from the spices, and the flavors never meld the way they do in dum cooking. It looks like biryani, but it tastes rushed. Dum-sealed biryani has a layered, integrated flavor that you can't fake with shortcuts.
            </p>
          </div>
        </div>

        <p className="!mt-10 text-sm text-ink-2/80 italic">
          Ready to try real dum-cooked biryani? <a href="/menu" className="text-ember underline hover:no-underline">Order now</a> for pickup or delivery in Omaha & Council Bluffs.
        </p>
      </div>
    </>
  );
}